// Custom Consent Banner Component with Google Consent Mode v2 support
// Matching the fe-site implementation to ensure consistency
import React, { useState, useEffect } from 'react';

const ConsentBanner = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true, cannot be disabled
        analytics: false,
        advertising: false,
        personalization: false
    });

    useEffect(() => {
        try {
            const consentData = localStorage.getItem('cookieConsent');
            if (!consentData) {
                setIsVisible(true);
            } else {
                // Load saved preferences and update gtag if they exist
                try {
                    const saved = JSON.parse(consentData);
                    if (saved.preferences) {
                        setPreferences(saved.preferences);
                        // Update consent on component mount if consent already exists
                        updateGtagConsent(saved.preferences);
                    }
                } catch (e) {
                    // Handle old format from react-cookie-consent library
                    if (consentData === 'granted') {
                        // Migrate old consent to new format with all permissions
                        const allGranted = {
                            necessary: true,
                            analytics: true,
                            advertising: true,
                            personalization: true
                        };
                        // Update to new format
                        try {
                            localStorage.setItem('cookieConsent', JSON.stringify({
                                timestamp: new Date().toISOString(),
                                preferences: allGranted,
                                type: 'migrated_from_old'
                            }));
                        } catch (storageError) {
                            console.warn('Unable to migrate consent format:', storageError);
                        }
                        setPreferences(allGranted);
                        updateGtagConsent(allGranted);
                    }
                }
            }
        } catch (e) {
            // localStorage access failed - show banner by default
            console.warn('Unable to access localStorage for consent banner:', e);
            setIsVisible(true);
        }
    }, []);

    const updateGtagConsent = (prefs) => {
        // Since we removed gtag from HTML, we only emit events for React to handle
        // React will manage all GA tracking based on these consent preferences

        // Emit custom event for ReactGA to listen to consent changes
        window.dispatchEvent(new CustomEvent('consentUpdated', { detail: prefs }));
    };

    const handleAcceptAll = () => {
        const allGranted = {
            necessary: true,
            analytics: true,
            advertising: true,
            personalization: true
        };

        try {
            localStorage.setItem('cookieConsent', JSON.stringify({
                timestamp: new Date().toISOString(),
                preferences: allGranted,
                type: 'accept_all'
            }));
        } catch (e) {
            console.error('Unable to save consent preferences:', e);
            // Continue with updating preferences in memory
        }

        setPreferences(allGranted);
        updateGtagConsent(allGranted);
        setIsVisible(false);
    };

    const handleRejectAll = () => {
        const onlyNecessary = {
            necessary: true,
            analytics: false,
            advertising: false,
            personalization: false
        };

        try {
            localStorage.setItem('cookieConsent', JSON.stringify({
                timestamp: new Date().toISOString(),
                preferences: onlyNecessary,
                type: 'reject_all'
            }));
        } catch (e) {
            console.error('Unable to save consent preferences:', e);
            // Continue with updating preferences in memory
        }

        setPreferences(onlyNecessary);
        updateGtagConsent(onlyNecessary);
        setIsVisible(false);
    };

    const handleSavePreferences = () => {
        try {
            localStorage.setItem('cookieConsent', JSON.stringify({
                timestamp: new Date().toISOString(),
                preferences: preferences,
                type: 'custom'
            }));
        } catch (e) {
            console.error('Unable to save consent preferences:', e);
            // Continue with updating preferences in memory
        }

        updateGtagConsent(preferences);
        setIsVisible(false);
        setShowDetails(false);
    };

    const togglePreference = (type) => {
        if (type === 'necessary') return; // Cannot disable necessary cookies
        setPreferences(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    if (!isVisible) return null;

    return (
        <div style={{
            position: 'fixed',
            bottom: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            color: 'black',
            padding: '16px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            fontSize: '13px',
            maxWidth: showDetails ? '600px' : '500px',
            width: '90%',
            zIndex: 10000,
            border: '1px solid #e0e0e0',
            transition: 'max-width 0.3s ease'
        }}>
            <div style={{ marginBottom: '12px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600' }}>
                    🍪 Cookie Settings
                </h3>
                <p style={{ margin: '0 0 8px', lineHeight: '1.4', color: '#666' }}>
                    We use cookies to enhance your experience, analyze traffic, and personalize content.
                    Please choose your preferences below.
                </p>
                <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: '#1976d2',
                        textDecoration: 'underline',
                        fontSize: '12px'
                    }}
                >
                    View Privacy Policy
                </a>
            </div>

            {showDetails && (
                <div style={{
                    margin: '16px 0',
                    padding: '12px',
                    background: '#f5f5f5',
                    borderRadius: '6px'
                }}>
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                            cursor: 'not-allowed',
                            opacity: 0.7
                        }}>
                            <input
                                type="checkbox"
                                checked={preferences.necessary}
                                disabled
                                style={{ marginRight: '8px' }}
                            />
                            <div>
                                <strong>Necessary Cookies</strong> (Always enabled)
                                <div style={{ fontSize: '11px', color: '#666' }}>
                                    Essential for website functionality
                                </div>
                            </div>
                        </label>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={preferences.analytics}
                                onChange={() => togglePreference('analytics')}
                                style={{ marginRight: '8px' }}
                            />
                            <div>
                                <strong>Analytics Cookies</strong>
                                <div style={{ fontSize: '11px', color: '#666' }}>
                                    Help us understand how visitors interact with our website
                                </div>
                            </div>
                        </label>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={preferences.advertising}
                                onChange={() => togglePreference('advertising')}
                                style={{ marginRight: '8px' }}
                            />
                            <div>
                                <strong>Advertising Cookies</strong>
                                <div style={{ fontSize: '11px', color: '#666' }}>
                                    Used to show relevant ads and measure ad effectiveness
                                </div>
                            </div>
                        </label>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={preferences.personalization}
                                onChange={() => togglePreference('personalization')}
                                style={{ marginRight: '8px' }}
                            />
                            <div>
                                <strong>Personalization Cookies</strong>
                                <div style={{ fontSize: '11px', color: '#666' }}>
                                    Remember your preferences and personalize your experience
                                </div>
                            </div>
                        </label>
                    </div>
                </div>
            )}

            <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                marginTop: '12px'
            }}>
                {!showDetails ? (
                    <>
                        <button
                            onClick={handleRejectAll}
                            style={{
                                backgroundColor: 'transparent',
                                color: '#666',
                                border: '1px solid #ccc',
                                padding: '8px 16px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease',
                                minWidth: '100px'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#f5f5f5';
                                e.target.style.borderColor = '#999';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.borderColor = '#ccc';
                            }}
                        >
                            Reject All
                        </button>

                        <button
                            onClick={() => setShowDetails(true)}
                            style={{
                                backgroundColor: '#f5f5f5',
                                color: 'black',
                                border: '1px solid #ccc',
                                padding: '8px 16px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease',
                                minWidth: '100px'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#e0e0e0';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = '#f5f5f5';
                            }}
                        >
                            Customize
                        </button>

                        <button
                            onClick={handleAcceptAll}
                            style={{
                                backgroundColor: '#1976d2',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s ease',
                                fontWeight: '500',
                                minWidth: '100px'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#1565c0'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
                        >
                            Accept All
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={() => setShowDetails(false)}
                            style={{
                                backgroundColor: 'transparent',
                                color: '#666',
                                border: '1px solid #ccc',
                                padding: '8px 16px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease',
                                minWidth: '80px'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                            }}
                        >
                            Back
                        </button>

                        <button
                            onClick={handleSavePreferences}
                            style={{
                                backgroundColor: '#1976d2',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s ease',
                                fontWeight: '500',
                                minWidth: '140px'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#1565c0'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
                        >
                            Save Preferences
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default ConsentBanner;